import { useCallback, useEffect, useRef, useState } from 'react';
// @ts-ignore
import { HiOutlineArrowDownTray, HiOutlineExclamationTriangle, HiOutlineEnvelope, HiOutlineXMark, HiOutlineCheckCircle } from 'react-icons/hi2';
import { triggerDesktopDownload, detectPlatform, desktopInstallerName, DesktopPlatform } from '../../constants/desktop';
import './DesktopDownloadModal.css';

const SUPPORT_EMAIL = 'jpande@gatekeepersystems.com';

type Step = { title: string; desc: string; warn?: string };

// Steps 2 and 3 are identical everywhere; only the install step differs, since
// Windows shows SmartScreen and macOS shows a drag-to-Applications window.
const COMMON_STEPS: Step[] = [
  {
    title: 'Sign in',
    desc: 'Launch Atlas Desktop and sign in with your Gatekeeper email address.',
  },
  {
    title: 'Open a brand in Claude',
    desc: 'Select any brand from the Opportunity table, then click "Open in Claude" to create an AI workspace with your data pre-loaded.',
  },
];

const STEPS: Record<DesktopPlatform, Step[]> = {
  windows: [
    {
      title: 'Run the installer',
      desc: 'Open atlas-setup.exe from your Downloads folder.',
      warn: 'Windows SmartScreen may appear — click "More info" then "Run anyway" to proceed.',
    },
    ...COMMON_STEPS,
  ],
  macos: [
    {
      title: 'Install the app',
      desc: 'Open atlas-setup.dmg from your Downloads folder, then drag Atlas into the Applications folder.',
      warn: 'macOS will block the first launch. On macOS 15 (Sequoia) and later, open System Settings → Privacy & Security, scroll to the message about Atlas and click "Open Anyway". On macOS 14 and earlier, right-click Atlas in Applications and choose "Open".',
    },
    ...COMMON_STEPS,
  ],
};

type Faq = { q: string; a: string };

const COMMON_TROUBLESHOOTING: Faq[] = [
  {
    q: 'My antivirus or browser blocked the download',
    a: 'The installer is a trusted Gatekeeper build. Choose "Keep" / "Allow" if your browser warns you, and add an exception in your antivirus if needed.',
  },
  {
    q: 'I can\'t sign in',
    a: 'Use your @gatekeepersystems.com email — the same account you use for the web app. If sign-in still fails, contact support below.',
  },
  {
    q: '"Open in Claude" does nothing',
    a: 'Make sure Claude Desktop is installed and you are signed in to it. Then retry "Open in Claude" from any brand.',
  },
];

const TROUBLESHOOTING: Record<DesktopPlatform, Faq[]> = {
  windows: [
    {
      q: '"Windows protected your PC" appears when I run the installer',
      a: 'This is Windows SmartScreen — it shows for any newly published app. Click "More info", then "Run anyway". Atlas is safe and signed by Gatekeeper Systems.',
    },
    ...COMMON_TROUBLESHOOTING,
    {
      q: 'The app won\'t open after installing',
      a: 'Atlas Desktop requires 64-bit Windows 10 or later. Restart your machine after install, then launch Atlas from the Start menu. If it still fails, reinstall using the latest download above.',
    },
  ],
  macos: [
    {
      q: '"Atlas cannot be opened because the developer cannot be verified"',
      a: 'macOS Gatekeeper shows this for apps it hasn\'t seen before. On macOS 15 (Sequoia) or later: open System Settings → Privacy & Security, scroll down to the message about Atlas, click "Open Anyway", then launch Atlas again and confirm. On macOS 14 or earlier: right-click (or Control-click) Atlas in Applications, choose "Open", then click "Open" again. You will need to repeat this after each update.',
    },
    ...COMMON_TROUBLESHOOTING,
    {
      q: 'The app won\'t open after installing',
      a: 'Atlas Desktop requires macOS 11 (Big Sur) or later on an Apple Silicon Mac. Make sure you dragged Atlas into Applications rather than running it from the disk image, then launch it from your Applications folder.',
    },
  ],
};

const PLATFORM_LABEL: Record<DesktopPlatform, string> = {
  windows: 'Windows',
  macos: 'macOS',
};

const REQUIREMENTS: Record<DesktopPlatform, string> = {
  windows: '64-bit Windows 10 or later · Free for Gatekeeper users',
  macos: 'macOS 11 or later, Apple Silicon · Free for Gatekeeper users',
};

interface Props {
  onClose: () => void;
}

export default function DesktopDownloadModal({ onClose }: Props) {
  // Which build the modal is showing. Defaults to the visitor's own OS, but they
  // can switch — people often download on one machine to install on another.
  const [platform, setPlatform] = useState<DesktopPlatform>(detectPlatform);
  const other: DesktopPlatform = platform === 'macos' ? 'windows' : 'macos';

  // Browsers give no event for download start/finish, so we optimistically show a
  // "downloading" state for a few seconds, then a "done" state. "Download again"
  // restarts it — useful if the download didn't kick off (popup blocker, etc.).
  const [phase, setPhase] = useState<'downloading' | 'done'>('downloading');
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const started = useRef(false);

  const startDownload = useCallback((target: DesktopPlatform) => {
    triggerDesktopDownload(target);
    setPlatform(target);
    setPhase('downloading');
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setPhase('done'), 5000);
  }, []);

  useEffect(() => {
    if (started.current) return; // guard against React StrictMode double-invoke
    started.current = true;
    startDownload(detectPlatform());
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [startDownload]);

  return (
    <div className="ddm-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="ddm-dialog">
        <button className="ddm-close" onClick={onClose} aria-label="Close">
          <HiOutlineXMark />
        </button>

        <div className="ddm-body">
        {/* Hero + download status */}
        <header className="ddm-hero">
          <h1>Get Atlas Desktop</h1>
          <p>AI-powered brand analysis, directly on your machine.</p>

          <div className="ddm-status">
            {phase === 'downloading' ? (
              <div className="ddm-downloading">
                <span className="ddm-spinner" />
                <span>Downloading Atlas Desktop…</span>
              </div>
            ) : (
              <div className="ddm-done">
                <HiOutlineCheckCircle className="ddm-done-icon" />
                <span>Your download should be ready — find <strong>{desktopInstallerName(platform)}</strong> in your Downloads folder.</span>
              </div>
            )}

            <button className="ddm-download-btn" onClick={() => startDownload(platform)}>
              <HiOutlineArrowDownTray />
              Download again
              <span className="ddm-platform-tag">{PLATFORM_LABEL[platform]}</span>
            </button>
            <span className="ddm-hint">If the download didn’t start automatically, use this button.</span>
            <button className="ddm-platform-switch" onClick={() => startDownload(other)}>
              Need the {PLATFORM_LABEL[other]} version instead?
            </button>
          </div>
          <span className="ddm-note">{REQUIREMENTS[platform]}</span>
        </header>

        {/* Video guide — removed for now; re-add the install walkthrough here later. */}

        {/* Installation steps */}
        <section className="ddm-section">
          <h2>Installation steps</h2>
          <div className="ddm-steps">
            {STEPS[platform].map((step, i) => (
              <div className="ddm-step" key={i}>
                <div className="ddm-step-num">{i + 1}</div>
                <div className="ddm-step-body">
                  <div className="ddm-step-title">{step.title}</div>
                  <div className="ddm-step-desc">{step.desc}</div>
                  {step.warn && (
                    <div className="ddm-step-warn">
                      <HiOutlineExclamationTriangle />
                      {step.warn}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Troubleshooting */}
        <section className="ddm-section">
          <h2>Troubleshooting</h2>
          <div className="ddm-faq">
            {TROUBLESHOOTING[platform].map((item, i) => (
              <details className="ddm-faq-item" key={i}>
                <summary>{item.q}</summary>
                <p>{item.a}</p>
              </details>
            ))}
          </div>
        </section>

        {/* Support */}
        <section className="ddm-support">
          <HiOutlineEnvelope className="ddm-support-icon" />
          <div>
            <div className="ddm-support-title">Still need help?</div>
            <div className="ddm-support-desc">
              Email our team at{' '}
              <a href={`mailto:${SUPPORT_EMAIL}?subject=Atlas Desktop Support`}>{SUPPORT_EMAIL}</a>
            </div>
          </div>
        </section>
        </div>
      </div>
    </div>
  );
}
